import { useQuery } from "@tanstack/react-query";
import { useApi } from "@aeriosiq/api-client";
import type {
  GetDataAcquisitionsParams,
  GetDataAcquisitionsResponse,
} from "@aeriosiq/types";

export const dataAcquisitionKeys = {
  all: ["data-acquisitions"] as const,
  lists: () => [...dataAcquisitionKeys.all, "list"] as const,
  list: (params: GetDataAcquisitionsParams) =>
    [...dataAcquisitionKeys.lists(), params] as const,
};

export function useDataAcquisitionsQuery(params: GetDataAcquisitionsParams) {
  const api = useApi();
  return useQuery<GetDataAcquisitionsResponse>({
    queryKey: dataAcquisitionKeys.list(params),
    queryFn: async () => {
      const response = await api.get<GetDataAcquisitionsResponse>(
        "/data-acquisitions",
        {
          params: toQueryParams(params),
          paramsSerializer: { indexes: null }, // repeat `state=X&state=Y`
        },
      );
      return response.data;
    },
    placeholderData: (prev) => prev, // keep previous page while new filters fetch
  });
}

function toQueryParams(p: GetDataAcquisitionsParams) {
  const out: Record<string, unknown> = {};
  if (p.page !== undefined) out.page = p.page;
  if (p.pageSize !== undefined) out.pageSize = p.pageSize;
  if (p.q) out.q = p.q;
  // SB3-CC-G (lyse/v1@6.0.0): unified single-axis `state[]` filter per
  // I5 supersedure. The legacy `evaluation_state[]` query param is
  // retired on the wire.
  if (p.state && p.state.length > 0) out.state = p.state;
  // SB6-DA-01 — only send when on; omitting it lets the BE default
  // (hide LOCKED + REJECTED) apply and keeps the URL clean.
  if (p.include_completed) out.include_completed = true;
  if (p.project_id && p.project_id.length > 0) out.project_id = p.project_id;
  if (p.site_id && p.site_id.length > 0) out.site_id = p.site_id;
  if (p.assignee_id && p.assignee_id.length > 0) {
    out.assignee_id = p.assignee_id;
  }
  if (p.date_from) out.date_from = p.date_from;
  if (p.date_to) out.date_to = p.date_to;
  // Story 017 — paired arrays so the contract's multi-column form is
  // forward-compatible even though the FE only emits one entry today.
  if (p.sort_by && p.sort_by.length > 0) out.sort_by = p.sort_by;
  if (p.sort_order && p.sort_order.length > 0) out.sort_order = p.sort_order;
  // TNCY-8 (lyse/v1@8.1.0): scope=member fans the list out across the
  // caller's granted cross-tenant projects' DAs. Only sent when explicitly
  // `member` — omitting it leaves the BE on its `own` default.
  if (p.scope === "member") out.scope = "member";
  // FCII (lyse/v1@8.3.0) — orphan filter. Only the two engaging values are
  // sent; omitting leaves the BE on its `all` default.
  if (p.linkage) out.linkage = p.linkage;
  return out;
}
